import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar, ArrowRight, Tag } from "lucide-react";

const BlogPage = () => {
    const [posts, setPosts] = useState([]);
    const [tags, setTags] = useState([]);
    const [activeTag, setActiveTag] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = { page };
                if (activeTag) params.tag = activeTag;
                const [postsRes, tagsRes] = await Promise.all([
                    publicApi.getBlogPosts(params),
                    publicApi.getBlogTags()
                ]);
                setPosts(postsRes.data.posts);
                setTotalPages(postsRes.data.pages);
                setTags(tagsRes.data);
            } catch (err) {
                console.error("Failed to load blog:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [page, activeTag]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    };

    return (
        <div className="min-h-screen" data-testid="blog-page">
            {/* Hero */}
            <section className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-20">
                    <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-4">
                        INDUSTRY <span className="text-primary">INSIGHTS</span>
                    </h1>
                    <p className="text-muted-foreground text-base lg:text-lg max-w-2xl">
                        Expert articles on dry ice blasting technology, equipment maintenance, industry best practices, and more.
                    </p>
                </div>
            </section>

            <div className="container mx-auto px-6 lg:px-12 py-12">
                {/* Tags filter */}
                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-tags-filter">
                        <Badge
                            variant={activeTag === null ? "default" : "outline"}
                            className="cursor-pointer rounded-none"
                            onClick={() => { setActiveTag(null); setPage(1); }}
                        >
                            All Posts
                        </Badge>
                        {tags.map(t => (
                            <Badge
                                key={t.tag}
                                variant={activeTag === t.tag ? "default" : "outline"}
                                className="cursor-pointer rounded-none"
                                onClick={() => { setActiveTag(t.tag); setPage(1); }}
                            >
                                <Tag className="h-3 w-3 mr-1" />
                                {t.tag} ({t.count})
                            </Badge>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1,2,3].map(i => (
                            <Card key={i} className="rounded-none border-border animate-pulse">
                                <div className="h-48 bg-secondary" />
                                <CardContent className="p-6">
                                    <div className="h-4 bg-secondary rounded w-1/3 mb-3" />
                                    <div className="h-6 bg-secondary rounded w-full mb-2" />
                                    <div className="h-4 bg-secondary rounded w-2/3" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-muted-foreground text-lg">No blog posts yet. Check back soon!</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {posts.map(post => (
                                <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
                                    <Card className="rounded-none border-border hover:border-primary/50 transition-all group h-full">
                                        {post.featured_image && (
                                            <div className="h-48 overflow-hidden">
                                                <img
                                                    src={resolveImageUrl(post.featured_image)}
                                                    alt={post.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                            </div>
                                        )}
                                        <CardContent className="p-6">
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                                                <Calendar className="h-3 w-3" />
                                                {formatDate(post.published_at)}
                                            </div>
                                            <h2 className="font-heading font-bold text-lg mb-2 group-hover:text-primary transition-colors line-clamp-2">
                                                {post.title}
                                            </h2>
                                            <p className="text-muted-foreground text-sm line-clamp-3">
                                                {post.excerpt}
                                            </p>
                                            <div className="flex items-center gap-1 mt-4 text-primary text-sm font-mono uppercase tracking-wider">
                                                Read More <ArrowRight className="h-4 w-4" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Link>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-12" data-testid="blog-pagination">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        className={`w-10 h-10 font-mono text-sm ${
                                            p === page ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:border-primary"
                                        }`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BlogPage;
